import { Box, BoxProps, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'

interface BigCounterProps extends Omit<BoxProps, 'children'> {
  value: number
  duration?: number
  label: string
  subtitle?: string
  isPercentage?: boolean
}

const BigCounter = ({
  value,
  duration = 850,
  label,
  subtitle,
  isPercentage = false,
  ...boxProps
}: BigCounterProps) => {
  const [displayValue, setDisplayValue] = useState(0)

  useEffect(() => {
    const startValue = 0
    setDisplayValue(0)

    const startTime = performance.now()
    let frameId: number

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const easedProgress = 1 - Math.pow(1 - progress, 5)
      const currentVal = startValue + (value - startValue) * easedProgress
      setDisplayValue(Math.max(0, currentVal))

      if (progress < 1) {
        frameId = requestAnimationFrame(animate)
      }
    }

    frameId = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [value, duration])

  const displayedNumber = Math.round(displayValue)
  const formattedNumber = displayedNumber.toLocaleString()

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="0.5rem"
      px="1.25rem"
      py="1.5rem"
      borderRadius="10px"
      border="1px solid"
      borderColor="border.default"
      bg="bg.default"
      {...boxProps}
    >
      <Text
        fontFamily="Inter"
        fontSize="12px"
        fontWeight="600"
        lineHeight="16px"
        letterSpacing="0.3px"
        textTransform="uppercase"
        color="fg.muted"
      >
        {label}
      </Text>

      <Text
        fontFamily="Inter"
        fontSize="30px"
        fontWeight="700"
        lineHeight="38px"
        color="fg.default"
      >
        {isPercentage ? `${formattedNumber}%` : formattedNumber}
      </Text>

      {subtitle && (
        <Text {...textStyles[textStyleKeys.Body3]} color="fg.muted">
          {subtitle}
        </Text>
      )}
    </Box>
  )
}

export default BigCounter
